import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";

const blankForm = {
  name: "Gunluk Pompa Kontrol Formu",
  targetType: "vehicle",
  vehicleType: "Pompa",
  period: "daily",
  active: true,
  blockPolicy: "warn",
  items: [
    { id: "yag-seviyesi", label: "Yag seviyesi", type: "ok_not_ok", createsIssue: true },
    { id: "bomlar", label: "Bomlar", type: "ok_not_ok", createsIssue: true },
    { id: "genel-aciklama", label: "Aciklama", type: "text", createsIssue: false },
  ],
};

const slug = (text) =>
  String(text || "")
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `madde-${Date.now()}`;

export default function FormBuilder() {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);

  const loadForms = async () => {
    const snap = await getDocs(collection(db, "kontrol_formlari"));
    setForms(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"))
    );
  };

  useEffect(() => { loadForms(); }, []);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "plates"), where("active", "==", true)));
      const seen = new Map();
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const value = String(data.aracCinsi || data.vehicleType || "").trim();
        if (!value) return;
        const key = value.toLocaleLowerCase("tr-TR");
        if (!seen.has(key)) seen.set(key, value);
      });
      setVehicleTypeOptions(Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "tr")));
    })();
  }, []);

  const updateItem = (index, patch) => {
    setForm((prev) => {
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), { id: `madde-${Date.now()}`, label: "", type: "ok_not_ok", createsIssue: true }],
    }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const resetForm = () => {
    setForm(blankForm);
    setEditingId("");
  };

  const saveForm = async () => {
    if (form.targetType === "vehicle" && String(form.vehicleType || "").trim() && !vehicleTypeOptions.includes(String(form.vehicleType || "").trim())) {
      return alert("Araç cinsi mevcut araç cinslerinden seçilmeli.");
    }
    const clean = {
      ...form,
      name: form.name.trim(),
      vehicleType: form.targetType === "vehicle" ? form.vehicleType.trim() : "",
      items: (form.items || [])
        .map((item) => ({
          id: item.id || slug(item.label),
          label: String(item.label || "").trim(),
          type: item.type || "ok_not_ok",
          createsIssue: item.createsIssue !== false,
        }))
        .filter((item) => item.label),
      updatedAt: serverTimestamp(),
      updatedBy: user?.email || "",
    };
    if (!clean.name) return alert("Form adi zorunlu.");
    if (!clean.items.length) return alert("En az bir kontrol maddesi ekleyin.");

    setLoading(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "kontrol_formlari", editingId), clean);
      } else {
        await addDoc(collection(db, "kontrol_formlari"), {
          ...clean,
          createdAt: serverTimestamp(),
          createdBy: user?.email || "",
        });
      }
      resetForm();
      await loadForms();
    } finally {
      setLoading(false);
    }
  };

  const editForm = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      targetType: item.targetType || "vehicle",
      vehicleType: item.vehicleType || "",
      period: item.period || "daily",
      active: item.active !== false,
      blockPolicy: item.blockPolicy || "warn",
      items: item.items || [],
    });
  };

  const deleteForm = async (id) => {
    if (!window.confirm("Form silinsin mi? Eski doldurulan kayitlar silinmez.")) return;
    await deleteDoc(doc(db, "kontrol_formlari", id));
    if (editingId === id) resetForm();
    await loadForms();
  };

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <h2 style={titleStyle}>{editingId ? "Form Duzenle" : "Form Olustur"}</h2>
        <div style={gridStyle}>
          <label>Form Adi<input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Hedef Tipi
            <select
              style={inputStyle}
              value={form.targetType}
              onChange={(e) => setForm({ ...form, targetType: e.target.value, vehicleType: e.target.value === "vehicle" ? form.vehicleType : "" })}
            >
              <option value="vehicle">Arac</option>
              <option value="site">Santiye</option>
              <option value="facility">Tesis</option>
            </select>
          </label>
          {form.targetType === "vehicle" && (
            <label>Arac Cinsi / Kapsam
              <input
                style={inputStyle}
                list="kontrol-vehicle-type-options"
                value={form.vehicleType}
                onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
                onBlur={() => {
                  if (form.vehicleType && !vehicleTypeOptions.includes(form.vehicleType)) {
                    setForm((prev) => ({ ...prev, vehicleType: "" }));
                  }
                }}
                placeholder="Araç cinsi seçin"
              />
              <datalist id="kontrol-vehicle-type-options">
                {vehicleTypeOptions.map((type) => <option key={type} value={type} />)}
              </datalist>
            </label>
          )}
          <label>Periyot
            <select style={inputStyle} value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}>
              <option value="daily">Gunluk</option>
              <option value="perUse">Kullanim/Dokum Oncesi</option>
            </select>
          </label>
          <label>Eksik kontrolde hazir beton davranisi
            <select style={inputStyle} value={form.blockPolicy} onChange={(e) => setForm({ ...form, blockPolicy: e.target.value })}>
              <option value="warn">Uyar ama devam et</option>
              <option value="block">Kaydi engelle</option>
              <option value="none">Kontrol etme</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Aktif
          </label>
        </div>

        <h3 style={subTitleStyle}>Kontrol Maddeleri</h3>
        {(form.items || []).map((item, index) => (
          <div key={item.id || index} style={itemRowStyle}>
            <input style={inputStyle} value={item.label} onChange={(e) => updateItem(index, { label: e.target.value })} placeholder="Madde adi" />
            <select style={inputStyle} value={item.type} onChange={(e) => updateItem(index, { type: e.target.value })}>
              <option value="ok_not_ok">Uygun / Uygun Degil</option>
              <option value="text">Metin</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={item.createsIssue !== false} onChange={(e) => updateItem(index, { createsIssue: e.target.checked })} />
              Uygun değilse kontrol eksikliği olarak takip et
            </label>
            <button type="button" onClick={() => removeItem(index)} style={dangerBtn}>Sil</button>
          </div>
        ))}
        <button type="button" onClick={addItem} style={secondaryBtn}>+ Madde</button>
        <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
          <button type="button" onClick={saveForm} disabled={loading} style={primaryBtn}>{loading ? "Kaydediliyor..." : "Kaydet"}</button>
          <button type="button" onClick={resetForm} style={secondaryBtn}>Temizle</button>
        </div>
      </section>

      <section style={panelStyle}>
        <h2 style={titleStyle}>Kayitli Formlar</h2>
        <div style={{ display: "grid", gap: 8 }}>
          {forms.map((item) => (
            <div key={item.id} style={formCardStyle}>
              <div>
                <b>{item.name}</b>
                <div style={mutedStyle}>{item.targetType} / {item.vehicleType || "-"} / {item.period} / {item.active === false ? "Pasif" : "Aktif"}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={() => editForm(item)} style={secondaryBtn}>Duzenle</button>
                <button type="button" onClick={() => deleteForm(item.id)} style={dangerBtn}>Sil</button>
              </div>
            </div>
          ))}
          {!forms.length && <div style={mutedStyle}>Kayitli form yok.</div>}
        </div>
      </section>
    </div>
  );
}

const pageStyle = { maxWidth: 1100, margin: "0 auto", display: "grid", gap: 12 };
const panelStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 };
const titleStyle = { margin: "0 0 12px", fontSize: 20 };
const subTitleStyle = { margin: "16px 0 8px", fontSize: 16 };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, marginTop: 4 };
const itemRowStyle = { display: "grid", gridTemplateColumns: "1.5fr 1fr auto auto", gap: 8, alignItems: "center", marginBottom: 8 };
const primaryBtn = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "9px 12px", cursor: "pointer" };
const secondaryBtn = { background: "#f8fafc", color: "#0f172a", border: "1px solid #cbd5e1", borderRadius: 6, padding: "8px 10px", cursor: "pointer" };
const dangerBtn = { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", cursor: "pointer" };
const formCardStyle = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 };
const mutedStyle = { color: "#64748b", fontSize: 13 };
